import * as React from "react";
import { withRouter, RouteComponentProps } from "react-router";
import * as cn from "classnames/bind";
import Select from "react-select";
import { actionCreators } from "page/calendar/store";
import { actionCreators as uiActionCreators } from "ui/store";
import {
    DefaultCulture,
    NavigationComponent,
    SvgCalendarLayout,
} from "ui/constants";
import { getDayModules, getWeekModules } from "ui/service";
import { actionCreators as eventActionCreators } from "event/store";
import CalendarFramePicker from "layout/common/controls-area/calendar-frame-picker";
import LayoutSelector from "layout/common/controls-area/layout-selector";
import CalendarDatePicker from "layout/common/controls-area/calendar-date-picker";
import ModuleSelector from "layout/common/controls-area/module-selector/module-selector";
import OwnerPage from "layout/common/controls-area/owner-page/owner-page";
import ZentaColorPicker from "layout/common/controls-area/zenta-color-picker";
import * as styles from "../controls-accordion.css";
import {
    CalendarState,
    OptionalDaySettings,
    BorderOptions,
    BurdeFontResponse,
    BurdeFontResource,
} from "burde";
import { Option } from "appz";
import ControlsContainer from "../controls-container";
import { useConfiguration } from "contexts/configuration-context";
import { useResources } from "contexts/resource-context";
import { Checkbox } from "util/components/checkbox";
import { CalendarType } from "svg/constants";
import NotebookCheckbox from "../notebook-checkbox";
import NotebookPageSlider from "../notebook-page-slider";
import { useEffect, useState } from "react";
import DatesetPicker from "../datesets/dateset-picker";

const cx = cn.bind(styles);

const subLabelCn = () =>
    cx({
        subLabel: true,
    });

const startMonthContainerCn = () =>
    cx({
        startMonthContainer: true,
    });

const componentContainerCn = () =>
    cx({
        componentContainer: true,
    });

type CalendarControlsAccordionProps = {
    calendar: CalendarState;
    optionalDaySettings: OptionalDaySettings;
    activeComponent: NavigationComponent;
    borderOptions: BorderOptions;
    customNumberOfEndPages: number;
    updateCalendarType: (calendarType: CalendarType) => void;
    updateCustomNumberOfEndPages: (customNumberOfEndPages: number) => void;
    calendarLayout: SvgCalendarLayout | null;
    fontOptions: BurdeFontResponse | null;
    selectedFont: BurdeFontResource | null;
} & typeof actionCreators &
    typeof uiActionCreators &
    typeof eventActionCreators;

type OptionalDayProps = {
    optionalDaySettings: any;
    updateOptionalDaySettings: any;
    calendar: CalendarState;
    calendarLayout: SvgCalendarLayout | null;
};

const OptionalDays: React.FC<OptionalDayProps> = (props) => {
    const configuration = useConfiguration();
    const resources = useResources();

    const toggleOptionalDaySetting = (settingName: string) => {
        props.optionalDaySettings[settingName] = !props.optionalDaySettings[
            settingName
        ];
        props.updateOptionalDaySettings(props.optionalDaySettings);
    };

    return (
        <div>
            {configuration.showNameDays && (
                <Checkbox
                    onClick={() => toggleOptionalDaySetting("showNameDays")}
                    label={resources["Layout_DayOptions_NameDays"]}
                    checked={props.optionalDaySettings.showNameDays}
                />
            )}

            {configuration.showMoonPhases && (
                <Checkbox
                    onClick={() => toggleOptionalDaySetting("showMoonPhases")}
                    label={resources["Layout_DayOptions_MoonPhases"]}
                    checked={props.optionalDaySettings.showMoonPhases}
                />
            )}

            {configuration.showFlagDays && (
                <Checkbox
                    onClick={() => toggleOptionalDaySetting("showFlagDays")}
                    label={resources["Layout_DayOptions_FlagDays"]}
                    checked={props.optionalDaySettings.showFlagDays}
                />
            )}

            {configuration.showHolidays && (
                <Checkbox
                    onClick={() => toggleOptionalDaySetting("showRedDays")}
                    label={resources["Layout_DayOptions_Holidays"]}
                    checked={props.optionalDaySettings.showRedDays}
                />
            )}

            {configuration.showOtherDays && (
                <Checkbox
                    onClick={() => toggleOptionalDaySetting("showOtherDays")}
                    label={resources["Layout_DayOptions_OtherDays"]}
                    checked={props.optionalDaySettings.showOtherDays}
                />
            )}

            {configuration.showTime &&
                props.calendarLayout != null &&
                props.calendarLayout.showTimestamps && (
                    <Checkbox
                        onClick={() =>
                            toggleOptionalDaySetting("showTimestamps")
                        }
                        label={resources["Layout_DayOptions_Time"]}
                        checked={props.optionalDaySettings.showTimestamps}
                    />
                )}
        </div>
    );
};

const CalendarControlsAccordion: React.FC<
    CalendarControlsAccordionProps & RouteComponentProps
> = (props) => {
    const [fontSets, setFontSets] = useState<Array<Option<any>>>([]);
    const resources = useResources();
    const {
        activeComponent,
        calendar,
        borderOptions,
        customNumberOfEndPages,
        updateCustomNumberOfEndPages,
        calendarLayout,
        fontOptions,
        selectedFont,
    } = props;

    useEffect(() => {
        setFontSets(getFontSets());
    }, [calendarLayout, fontOptions]);

    const changeFrameColor = (colorItemId: any) => {
        const selectedColor = borderColorOptions.find(
            (x) => x.id === colorItemId,
        );
        props.updateUserHasChangedBorder(true);
        props.updateBorderColor(selectedColor ? selectedColor.hexColor : null);
        props.updateBorderImageId(null);
    };

    const changeCustomFrameColor = (color: string) => {
        props.updateUserHasChangedBorder(true);
        props.updateBorderColor(color);
        props.updateBorderImageId(null);
    };

    const borderColorOptions = borderOptions
        ? borderOptions.solidColors.map((color, index) => {
              return {
                  id: index + 1,
                  hexColor: color,
              };
          })
        : [];

    const getSelectedFrame = () => {
        if (!!props.calendar.borderColor) {
            const selectedColor = borderColorOptions.find(
                (x) => x.hexColor === props.calendar.borderColor,
            );
            return selectedColor ? selectedColor.id : -1;
        }
        return null;
    };

    const getFontSets = (): Array<Option<any>> => {
        const layout = props.calendarLayout;
        const fontSets: Array<Option<any>> = [];

        if (layout && fontOptions) {
            fontOptions.fontResources.forEach((font) => {
                const foundFont = layout.fonts.find((x) => x === font.title);
                if (foundFont) {
                    fontSets.push({ label: font.title, value: font.id });
                }
            });
        }

        return fontSets;
    };

    const getLines = (): Array<Option<any>> => {
        const layout = props.calendarLayout;
        const lineOptions: Array<Option<any>> = [];

        if (layout && layout.lines) {
            layout.lines.forEach((line) => {
                lineOptions.push({ label: line, value: line });
            });
        }

        return lineOptions;
    };

    const updateFont = (id: number) => {
        props.updateFontId(id);
    };

    const updateLines = (lineStyle: string) => {
        props.updateLines(lineStyle);
    };

    return (
        <>
            {activeComponent.name === "ownerPage" && (
                <ControlsContainer Title={activeComponent.resourceKey}>
                    <OwnerPage />
                </ControlsContainer>
            )}

            {activeComponent.name === "layout" && (
                <ControlsContainer Title={activeComponent.resourceKey}>
                    {calendar.calendarType !== CalendarType.Notebook && (
                        <div className={startMonthContainerCn()}>
                            <label className={subLabelCn()}>
                                {resources["Layout_StartingMonth"]}
                            </label>
                            <CalendarDatePicker />
                        </div>
                    )}
                    <div
                        className={componentContainerCn()}
                        style={{ paddingBottom: "20px" }}>
                        {/*
                        TODO (JH): Activate this again on customer demand for dynamic format selection.
                        <NotebookCheckbox
                            isNotebook={
                                calendar.calendarType === CalendarType.Notebook
                            }
                            updateCalendarType={updateCalendarType}
                        />
                        */}
                        <LayoutSelector />

                        {fontSets.length > 0 &&
                            (selectedFont || calendar.fontSetName) && (
                                <>
                                    <label className={subLabelCn()}>
                                        {resources["Layout_Font"]}
                                    </label>
                                    {calendar.fontSetName && (
                                        <Select
                                            isSearchable={false}
                                            options={fontSets}
                                            value={{
                                                label: calendar.fontSetName,
                                                value: 0,
                                            }}
                                            onChange={(selectedValue) => {
                                                updateFont(
                                                    (selectedValue as any)
                                                        .value,
                                                );
                                            }}
                                        />
                                    )}
                                    {selectedFont && !calendar.fontSetName && (
                                        <Select
                                            isSearchable={false}
                                            options={fontSets}
                                            value={{
                                                label: selectedFont.title,
                                                value: selectedFont.id,
                                            }}
                                            onChange={(selectedValue) => {
                                                updateFont(
                                                    (selectedValue as any)
                                                        .value,
                                                );
                                            }}
                                        />
                                    )}
                                    <br />
                                </>
                            )}
                        {calendar.calendarType !== CalendarType.Notebook && (
                            <>
                                <label className={subLabelCn()}>
                                    {resources["Layout_Lines"]}
                                </label>
                                <Select
                                    isSearchable={false}
                                    options={getLines()}
                                    value={{
                                        label: props.calendar.lines,
                                        value: props.calendar.lines,
                                    }}
                                    onChange={(selectedItem) =>
                                        updateLines((selectedItem as any).value)
                                    }
                                />
                                <br />
                                <label className={subLabelCn()}>
                                    {resources["Layout_DayOptions"]}
                                </label>
                                <OptionalDays
                                    optionalDaySettings={
                                        props.optionalDaySettings
                                    }
                                    updateOptionalDaySettings={
                                        props.updateOptionalDaySettings
                                    }
                                    calendar={props.calendar}
                                    calendarLayout={
                                        props.calendarLayout
                                    }></OptionalDays>
                            </>
                        )}
                        {calendar.calendarType === CalendarType.Notebook && (
                            <NotebookPageSlider
                                calendarSize={calendar.calendarSize}
                                customNumberOfEndPages={customNumberOfEndPages}
                                updateCustomNumberOfEndPages={
                                    updateCustomNumberOfEndPages
                                }
                            />
                        )}
                    </div>
                </ControlsContainer>
            )}

            {activeComponent.name === "frame" && (
                <ControlsContainer Title={activeComponent.resourceKey}>
                    <label className={subLabelCn()}>
                        {resources["Frame_Color"]}
                    </label>
                    <ZentaColorPicker
                        selectedItemId={getSelectedFrame()}
                        bigButtons={true}
                        colors={borderColorOptions}
                        onClick={changeFrameColor}
                        colorsWithBorder={[1]}
                        useColorPicker={
                            borderOptions && borderOptions.colorPicker
                        }
                        setCustomColor={changeCustomFrameColor}
                        selectedColor={props.calendar.borderColor as any}
                    />
                    <label className={subLabelCn()}>
                        {resources["Frame_Pattern"]}
                    </label>
                    <CalendarFramePicker />
                </ControlsContainer>
            )}

            {activeComponent.name === "dayModules" && (
                <ControlsContainer
                    Title={
                        calendar.calendarType === CalendarType.Notebook
                            ? resources["Notebook_DayModules"]
                            : activeComponent.resourceKey
                    }>
                    <ModuleSelector
                        addModule={props.addDayModule}
                        removeModule={props.removeDayModule}
                        getModules={getDayModules}
                        culture={
                            calendar.culture !== null
                                ? calendar.culture
                                : DefaultCulture
                        }
                        selectedModules={calendar.dayModules}
                        maxModules={
                            calendarLayout ? calendarLayout.maxDayModules : 0
                        }
                        calendarSize={calendar.calendarSize}
                        calendarType={calendar.calendarType}
                    />
                </ControlsContainer>
            )}

            {activeComponent.name === "weekModules" && (
                <ControlsContainer
                    Title={
                        calendar.calendarType === CalendarType.Notebook
                            ? resources["Notebook_WeekModules"]
                            : activeComponent.resourceKey
                    }>
                    <ModuleSelector
                        addModule={props.addWeekModule}
                        removeModule={props.removeWeekModule}
                        getModules={getWeekModules}
                        culture={
                            calendar.culture !== null
                                ? calendar.culture
                                : DefaultCulture
                        }
                        selectedModules={calendar.weekModules}
                        maxModules={
                            calendarLayout ? calendarLayout.maxWeekModules : 0
                        }
                        calendarSize={calendar.calendarSize}
                        calendarType={calendar.calendarType}
                    />
                </ControlsContainer>
            )}

            {activeComponent.name === "personalDates" && (
                <ControlsContainer Title={activeComponent.resourceKey}>
                    <DatesetPicker />
                </ControlsContainer>
            )}
        </>
    );
};

export default withRouter(CalendarControlsAccordion);
